import useEventListener from '@use-it/event-listener';
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { MouseEvent as ReactMouseEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Markdown } from '../../utils/markdown';
import classNames from '../../utils/classNames';
import { RoundedButton } from '../form/Button';
import Input, { INPUT_SIZE } from '../form/Input';

export const ConfirmType = Object.freeze({
    PROMPT: 'prompt',
    INFO: 'info',
    CONFIRM: 'confirm',
} as const);

export const confirmOverlayClass = 'confirm-overlay';
export const confirmContentClass = 'confirm-dialog__content';
export const confirmBodyLockedClass = 'confirm-overlay-body-locked';

const DEFAULT_CLOSE_KEYS = Object.freeze(['Escape'] as const);
type CloseKey = (typeof DEFAULT_CLOSE_KEYS)[number];

export const containerClasses = `${confirmContentClass} w-80 flex-col space-y-3 rounded-lg bg-white p-3 text-center break-words`;
// Long messages scroll, so the buttons below them stay on screen.
const messageClasses = 'max-h-[60vh] overflow-y-auto';

type ConfirmationHandler = (value?: string) => void;
type ConfirmVariant = (typeof ConfirmType)[keyof typeof ConfirmType];
type ConfirmCloseHandler = () => void;

interface SharedConfirmOptions {
    onConfirm?: ConfirmationHandler;
    confirmLabel?: string;
    onCancel?: () => void;
    afterClose?: () => void;
    closeOnClickOutside?: boolean;
    closeOnEscape?: boolean;
    keyCodeForClose?: number[];
    hideIcon?: boolean;
    icon?: IconProp;
    body?: ReactNode;
    message?: ReactNode;
}

interface ConfirmUIProps extends SharedConfirmOptions {
    onClose: () => void;
}

interface ConfirmOverlayProps {
    children: ReactElement;
    onClose: () => void;
    forceRead?: boolean;
}

export interface ConfirmOptions extends SharedConfirmOptions {
    markdownMessage?: string;
    type?: ConfirmVariant;
    forceRead?: boolean;
}

// type ConfirmFunction = (options: ConfirmOptions) => () => void;

export function Info({ message, onClose, body, hideIcon = false }: ConfirmUIProps) {
    return (
        <div className={containerClasses}>
            {!hideIcon && <FontAwesomeIcon icon={faCircleInfo} size="2xl" />}
            {body || <div className={messageClasses}>{message}</div>}
            <div className="space-x-3">
                <RoundedButton id="info-button" type="button" onClick={onClose} variant="big" flex>
                    {gettext('OK')}
                </RoundedButton>
            </div>
        </div>
    );
}

export function Confirm({
    message,
    onConfirm,
    confirmLabel = gettext('CONFIRM'),
    onCancel,
    onClose,
    body,
    hideIcon = false,
    icon,
}: ConfirmUIProps) {
    const buttons: Array<{
        id: string;
        label: string;
        color: 'primary' | 'secondary';
        onClick?: ConfirmationHandler;
    }> = [
        {
            id: 'alert-confirm-button',
            label: confirmLabel,
            onClick: onConfirm,
            color: 'primary',
        },
        {
            id: 'alert-cancel-button',
            label: gettext('CANCEL'),
            onClick: onCancel,
            color: 'secondary',
        },
    ];

    return (
        <div className={containerClasses}>
            {!hideIcon && <FontAwesomeIcon icon={icon ?? faTriangleExclamation} size="2xl" />}
            {body || <div className={messageClasses}>{message}</div>}
            <div className="space-x-3">
                {buttons.map(({ id, label, color, onClick }) => (
                    <RoundedButton
                        id={id}
                        key={id}
                        type="button"
                        onClick={() => {
                            onClick?.();
                            onClose?.();
                        }}
                        variant="big"
                        color={color}
                        flex
                    >
                        {label}
                    </RoundedButton>
                ))}
            </div>
        </div>
    );
}
export function Prompt({ message, onConfirm, onClose, ...props }: ConfirmUIProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    function confirmWithInput() {
        const value = inputRef.current?.value ?? '';
        onConfirm?.(value);
    }

    const body = (
        <>
            <p id="prompt-label">{message}</p>
            <p>
                <Input size={INPUT_SIZE.SMALL} aria-labelledby="prompt-label" ref={inputRef} />
            </p>
        </>
    );

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                confirmWithInput();
                onClose?.();
            }}
        >
            <Confirm
                body={body}
                onConfirm={confirmWithInput}
                hideIcon={true}
                icon={faCircleInfo}
                onClose={onClose}
                {...props}
            />
        </form>
    );
}
const TYPE = Object.freeze({
    prompt: Prompt,
    info: Info,
    confirm: Confirm,
});

function ConfirmOverlay({ children, onClose, forceRead }: ConfirmOverlayProps) {
    const overlayRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        document.body.classList.add(confirmBodyLockedClass);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.classList.remove(confirmBodyLockedClass);
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    // When forceRead is active the dialog should not close in response to keyboard shortcuts.
    const normalizedKeys = useMemo<ReadonlyArray<CloseKey>>(
        () => (forceRead ? [] : DEFAULT_CLOSE_KEYS),
        [forceRead]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            const { key } = event;

            if (key && normalizedKeys.some((allowedKey) => allowedKey === key)) {
                event.stopPropagation();
                onClose();
            }
        },
        [normalizedKeys, onClose]
    );

    useEventListener('keydown', handleKeyDown);

    // Close the dialog when the backdrop itself is clicked, unless forceRead keeps it locked open.
    const handlePointerDown = useCallback(
        (event: ReactMouseEvent<HTMLDivElement>) => {
            if (forceRead) {
                return;
            }

            if (event.target === overlayRef.current) {
                onClose();
            }
        },
        [forceRead, onClose]
    );

    return (
        <div
            ref={overlayRef}
            className={classNames(
                confirmOverlayClass,
                'z-modal-overlay fixed inset-0 flex items-center justify-center bg-black/50'
            )}
            onMouseDown={handlePointerDown}
            role="dialog"
            aria-modal="true"
        >
            {children}
        </div>
    );
}
export function renderOverlay({
    forceRead,
    afterClose,
    renderContent,
}: {
    forceRead: boolean;
    afterClose?: () => void;
    renderContent: ({ onClose }: { onClose: () => void }) => ReactElement;
}): ConfirmCloseHandler {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const root = createRoot(container);
    let didClose = false;

    function handleClose() {
        if (didClose) {
            return;
        }

        didClose = true;
        setTimeout(() => {
            root.unmount();
            if (container.parentNode) {
                container.parentNode.removeChild(container);
            }
            afterClose?.();
        }, 0);
    }

    root.render(
        <ConfirmOverlay onClose={handleClose} forceRead={forceRead}>
            {renderContent({ onClose: handleClose })}
        </ConfirmOverlay>
    );

    return handleClose;
}

// Method that is able to be used outside of React. Use `useConfirm` when using this inside React.
export function confirm({
    message,
    markdownMessage,
    onConfirm,
    confirmLabel,
    onCancel,
    forceRead = false,
    afterClose,
    type = ConfirmType.CONFIRM,
    hideIcon,
}: ConfirmOptions): ConfirmCloseHandler {
    const UI = TYPE[type];
    const close = renderOverlay({
        forceRead,
        afterClose,
        renderContent: ({ onClose }) => (
            <UI
                hideIcon={hideIcon}
                onConfirm={onConfirm}
                confirmLabel={confirmLabel}
                onCancel={onCancel}
                onClose={onClose}
                message={markdownMessage ? <Markdown>{markdownMessage}</Markdown> : message}
            />
        ),
    });

    // Return a function that will close the confirm when executed.
    return close;
}

// Hook that returns a wrapped `confirm` method. The `confirm` wrapper will store the
// `onClose` method and will call that automatically when the component gets unmounted.
export function useConfirm() {
    const onCloseReferences = useRef<Array<ConfirmCloseHandler>>([]);

    useEffect(
        () => () => {
            // Call all stored onClose methods on unmount.
            onCloseReferences.current.forEach((onClose) => onClose());
        },
        []
    );

    return useCallback((options: ConfirmOptions) => {
        // Store the onClose method so that we can call that on unmount.
        const close = confirm(options);
        onCloseReferences.current.push(close);
        return close;
    }, []);
}
